import React from 'react';
import { createRoot } from 'react-dom/client';
import { EventsCarousel } from '../src/components/EventsCarousel';
import type { EventsCarouselProps } from '../src/components/EventsCarousel';
import styleText from '../src/app/globals.css?inline';
import { attr, parseDataParams } from './params';

function detectAssetBase(): string {
  try {
    const scriptSrc = document.currentScript?.getAttribute('src');
    if (scriptSrc) {
      const url = new URL(scriptSrc, window.location.href);
      return url.href.replace(/\/[^/]*$/, '');
    }
  } catch {}
  return '';
}

interface WidgetConfig extends EventsCarouselProps {
  containerId?: string;
}

declare global {
  interface Window {
    EVENTS_CAROUSEL_CONFIG?: WidgetConfig;
    EventsCarouselWidget?: EventsCarouselWidgetInstance;
  }
}

class EventsCarouselWidgetInstance {
  private root: ReturnType<typeof createRoot> | null = null;
  private config: WidgetConfig;

  constructor(config: WidgetConfig = {}) {
    this.config = {
      containerId: 'events-carousel-root',
      ...config,
    };
  }

  init() {
    const container = document.getElementById(this.config.containerId!);
    if (!container) {
      console.error(`Events Carousel: Container #${this.config.containerId} not found`);
      return;
    }

    const shadowRoot = container.shadowRoot ?? container.attachShadow({ mode: 'open' });

    const mountPoint = document.createElement('div');
    mountPoint.id = 'events-carousel-app';
    shadowRoot.appendChild(mountPoint);

    // Inject font in document head
    const fontId = 'events-carousel-montserrat';
    if (!document.getElementById(fontId)) {
      const link = document.createElement('link');
      link.id = fontId;
      link.rel = 'stylesheet';
      link.href =
        'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap';
      document.head.appendChild(link);
    }

    // Inject styles into shadow DOM
    const resetCSS = `
      *, *::before, *::after { box-sizing: border-box; }
      :host { display: block; width: 100%; }
      #events-carousel-app { width: 100%; }
    `;
    let shadowCSS = styleText
      .replace(/:root\b/g, ':host')
      .replace(/\bbody\b/g, '#events-carousel-app');
    const style = document.createElement('style');
    style.textContent = resetCSS + '\n' + shadowCSS;
    shadowRoot.appendChild(style);

    // Read config from data attributes. Precedence: explicit config object >
    // dedicated data-* attribute > the shared data-params string.
    const fromParams = parseDataParams(container.getAttribute('data-params'));

    const campus = this.config.campus ?? attr(container.dataset.campus) ?? fromParams.campus;
    const ministryId =
      this.config.ministryId ??
      (attr(container.dataset.ministryId)
        ? Number(container.dataset.ministryId)
        : fromParams.ministryId);
    const programId =
      this.config.programId ??
      (attr(container.dataset.programId)
        ? Number(container.dataset.programId)
        : fromParams.programId);
    const search = this.config.search ?? attr(container.dataset.search) ?? fromParams.search;
    const viewAllUrl = this.config.viewAllUrl ?? attr(container.dataset.viewAllUrl);

    this.root = createRoot(mountPoint);
    this.root.render(
      <React.StrictMode>
        <div style={{ padding: '0 2rem' }}>
          <EventsCarousel
            apiBaseUrl={this.config.apiBaseUrl}
            campus={campus}
            ministryId={ministryId}
            programId={programId}
            search={search}
            viewAllUrl={viewAllUrl}
          />
        </div>
      </React.StrictMode>
    );
  }

  destroy() {
    this.root?.unmount();
    this.root = null;
  }
}

if (typeof window !== 'undefined') {
  // API base is the centralized api.woodsidebible.org host, baked at build
  // time from CHURCH_HUB_API_URL. (Do NOT derive it from the script origin —
  // the script is served from widgets.woodsidebible.org, which has no /api
  // routes, so that 404s and the widget silently falls back to sample data.)
  // window.EVENTS_CAROUSEL_CONFIG can still override for local testing.
  const scriptOrigin = (() => {
    try {
      const src = document.currentScript?.getAttribute('src');
      if (src) return new URL(src, window.location.href).origin;
    } catch {}
    return window.location.origin;
  })();

  const config: WidgetConfig = {
    apiBaseUrl: process.env.CHURCH_HUB_API_URL || scriptOrigin,
    containerId: 'events-carousel-root',
    ...(window.EVENTS_CAROUSEL_CONFIG || {}),
  };

  if (!window.EventsCarouselWidget) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        const widget = new EventsCarouselWidgetInstance(config);
        widget.init();
        window.EventsCarouselWidget = widget;
      });
    } else {
      const widget = new EventsCarouselWidgetInstance(config);
      widget.init();
      window.EventsCarouselWidget = widget;
    }
  }
}

export default EventsCarouselWidgetInstance;
